import type { StorefrontDeviceClass, StorefrontSourceCategory } from "./types";

const SESSION_STORAGE_PREFIX = "meucontroleai:storefront-analytics:session:";

/** Best-effort random id. `crypto.randomUUID` first, a non-cryptographic fallback for older/embedded webviews. */
export function generateEventId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the fallback generator below
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Random UUID per tab session, per merchant, in `sessionStorage` only — no
 * cookie, no `localStorage`, cleared with the tab. Reused across every sink
 * instance and every remount for the lifetime of that tab.
 */
export function getOrCreateSessionId(merchantId: string): string {
  const key = `${SESSION_STORAGE_PREFIX}${merchantId}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const created = generateEventId();
    sessionStorage.setItem(key, created);
    return created;
  } catch {
    return generateEventId();
  }
}

/** True once per tab session per merchant — backs the `session_start` dedupe without depending on component lifecycle. */
export function consumeSessionStartFlag(merchantId: string): boolean {
  const key = `${SESSION_STORAGE_PREFIX}${merchantId}:started`;
  try {
    if (sessionStorage.getItem(key) === "1") return true;
    sessionStorage.setItem(key, "1");
    return false;
  } catch {
    return false;
  }
}

/** `navigator.globalPrivacyControl` or `doNotTrack: "1"` — the sink must do nothing at all. */
export function isTrackingOptedOut(): boolean {
  try {
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
    if (nav.globalPrivacyControl === true) return true;
    if (nav.doNotTrack === "1") return true;
  } catch {
    return false;
  }
  return false;
}

const UTM_SOURCE_MAP: Record<string, StorefrontSourceCategory> = {
  whatsapp: "whatsapp",
  wa: "whatsapp",
  instagram: "instagram",
  ig: "instagram",
  facebook: "facebook",
  fb: "facebook",
  google: "search",
  bing: "search",
  qr: "qr_code",
  qrcode: "qr_code",
  qr_code: "qr_code",
};

function classifyFromUtmSource(): StorefrontSourceCategory | null {
  const raw = new URLSearchParams(window.location.search).get("utm_source");
  if (!raw) return null;
  return UTM_SOURCE_MAP[raw.trim().toLowerCase()] ?? null;
}

function classifyFromReferrer(): StorefrontSourceCategory {
  const referrer = document.referrer;
  if (!referrer) return "direct";

  let hostname: string;
  try {
    hostname = new URL(referrer).hostname.toLowerCase();
  } catch {
    return "other";
  }

  if (hostname === window.location.hostname) return "direct";
  if (hostname.includes("wa.me") || hostname.includes("whatsapp")) return "whatsapp";
  if (hostname.includes("instagram")) return "instagram";
  if (hostname.includes("facebook") || hostname.includes("m.facebook") || hostname === "fb.com" || hostname.endsWith(".fb.com")) return "facebook";
  if (
    hostname.includes("google") ||
    hostname.includes("bing") ||
    hostname.includes("duckduckgo") ||
    hostname.includes("yahoo")
  ) {
    return "search";
  }
  return "other";
}

/** Client-side source category: `utm_source` first, `document.referrer` hostname otherwise. Only the category ever leaves the browser. */
export function classifySourceCategory(): StorefrontSourceCategory {
  try {
    return classifyFromUtmSource() ?? classifyFromReferrer();
  } catch {
    return "direct";
  }
}

type UserAgentData = {
  brands?: ReadonlyArray<{ brand: string; version: string }>;
  platform?: string;
};

function userAgentData(): UserAgentData | null {
  try {
    return (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData ?? null;
  } catch {
    return null;
  }
}

/** Browser family and major version. The raw user-agent string is never returned. */
export function classifyBrowser(): string {
  const brands = userAgentData()?.brands ?? [];
  const chosen = brands.find((item) => /^(Google Chrome|Microsoft Edge|Opera|Firefox|Safari|Chrome)$/i.test(item.brand));
  if (!chosen?.brand || !chosen.version) return "";
  return `${chosen.brand.replace(/^Google /, "")} ${chosen.version}`.slice(0, 32);
}

/** OS family from client hints, without the version or device model. */
export function classifyPlatform(): string {
  const platform = userAgentData()?.platform ?? "";
  return platform.slice(0, 32);
}

/** Coarse device class from viewport width + pointer/touch heuristics. No user-agent string is ever read for this. */
export function classifyDeviceClass(): StorefrontDeviceClass {
  try {
    const width = window.innerWidth || 0;
    const coarsePointer = window.matchMedia?.("(pointer: coarse)").matches ?? false;

    if (coarsePointer) return width >= 768 ? "tablet" : "mobile";
    return width >= 1024 ? "desktop" : width >= 768 ? "tablet" : "mobile";
  } catch {
    return "desktop";
  }
}
